import { Head, Link } from '@inertiajs/react';
import { ExternalLink, MapPin, Users } from 'lucide-react';
import { AvatarCircle } from '@/components/admin/avatar-circle';
import { EmailsCard } from '@/components/admin/emails-card';
import { githubUsername } from '@/components/admin/github-username';
import { Github } from '@/components/icons';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import type { AdminHackersGlobalShow } from '@/types';

function formatDate(timestamp: string) {
  return new Date(timestamp).toLocaleDateString('en-US', { dateStyle: 'medium' });
}

const SUBMISSION_STATUS_LABELS: Record<string, string> = {
  received: 'Received',
  priority_waiting: 'Priority waiting',
  asking_self_finance_trip: 'Asking self-finance trip',
  approved: 'Approved',
  onboarding_request: 'Onboarding requested',
  onboarding_expired: 'Onboarding expired',
  onboarding_complete: 'Onboarding complete',
  rejected: 'Rejected',
  waiting_list: 'Waiting list',
  withdrawn: 'Withdrawn',
  archived: 'Archived',
  draft: 'Draft',
};

const ACCEPTED_STATUSES = new Set([
  'approved',
  'onboarding_request',
  'onboarding_expired',
  'onboarding_complete',
]);

export default function GlobalShow({
  hacker,
  profiles,
  emails,
  title,
}: AdminHackersGlobalShow) {
  return (
    <>
      <Head title={title} />
      <div className="container mx-auto max-w-4xl space-y-6 py-8">
        <div className="flex items-center gap-4">
          <AvatarCircle src={hacker.avatarUrl} name={hacker.fullName} size="lg" />
          <div>
            <h1 className="font-bold text-3xl tracking-tight">
              {hacker.fullName}
            </h1>
            <a
              href={`https://github.com/${githubUsername(hacker.github)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 flex items-center gap-1 text-blue-600 text-sm hover:text-blue-800"
            >
              <Github className="h-3.5 w-3.5" />@{githubUsername(hacker.github)}
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>
              Editions ({profiles.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {profiles.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground">
                <p>No submissions on record for this hacker.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {profiles.map((profile) => (
                  <div
                    key={profile.id}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-4"
                  >
                    <div className="space-y-1">
                      <Link
                        href={`/admin/${profile.submission.event.slug}/${githubUsername(hacker.github)}`}
                        className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        {profile.submission.event.name}
                      </Link>
                      <div className="flex flex-wrap items-center gap-2 text-muted-foreground text-sm">
                        <Badge
                          variant={
                            ACCEPTED_STATUSES.has(profile.submission.status)
                              ? 'secondary'
                              : 'outline'
                          }
                        >
                          {SUBMISSION_STATUS_LABELS[profile.submission.status] ??
                            profile.submission.status}
                        </Badge>
                        <span>
                          Submitted {formatDate(profile.submission.submittedAt)}
                        </span>
                      </div>
                    </div>

                    {profile.team ? (
                      <Link
                        href={`/admin/${profile.submission.event.slug}/teams/${profile.team.slug}`}
                        className="flex items-center gap-1.5 text-blue-600 text-sm hover:text-blue-800"
                      >
                        <Users className="h-3.5 w-3.5" />
                        {profile.team.slug}
                        {profile.team.tableNumber && (
                          <span className="flex items-center gap-0.5 text-muted-foreground">
                            <MapPin className="h-3 w-3" />
                            {profile.team.tableNumber}
                          </span>
                        )}
                      </Link>
                    ) : (
                      <span className="text-muted-foreground text-sm">
                        No team
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <EmailsCard
          emails={emails}
          emptyMessage="No emails sent to this hacker"
          showRecipient={false}
        />
      </div>
    </>
  );
}
